import type { HTMLAttributes, PropsWithChildren } from 'react';
import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Prevents DarkReader from modifying children by isolating them inside a
 * closed Shadow DOM boundary. The host `<span>` remains styleable by the
 * document; only the shadow contents are protected.
 *
 * This should be used sparingly and should never wrap complex or interactive
 * parts of the UI. It is intended to provide an escape hatch for very specific
 * edge cases involving individual elements, such as SVGs.
 */
export const DarkReaderIgnore = ({
  children,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLSpanElement>>) => {
  const [shadow, setShadow] = useState<ShadowRoot>();
  const shadowRef = useRef<ShadowRoot>();

  const ref = useCallback((node: HTMLSpanElement | null) => {
    if (!node || shadowRef.current) return;

    const shadowRoot = node.attachShadow({ mode: 'closed' });
    shadowRef.current = shadowRoot;
    setShadow(shadowRoot);
  }, []);

  return (
    <span ref={ref} {...props}>
      {shadow && createPortal(children, shadow)}
    </span>
  );
};
